import FormHeader from "@/components/FormHeader";
import InputError from "@/components/InputError";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import useSessionStorage from "@/hooks/useSessionStorage";
import api from "@/lib/axios";
import { DataBerkas, SysBerkas } from "@/types";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import {
  AlertCircle,
  FileText,
  Loader2,
  Trash2,
  UploadCloud,
} from "lucide-react";
import { useRef, useState } from "react";
import toast from "react-hot-toast";

// ... (API functions tetap sama) ...
const postData = async (newData: FormData) => {
  const id_sys_periode = newData.get("id_sys_periode");
  const response = await api.post(
    `/siswa/penerimaan/smbjm/data-berkas/upload?id_sys_periode=${id_sys_periode}`,
    newData,
    { headers: { "Content-Type": "multipart/form-data" } },
  );
  return response.data;
};
const deleteData = async (newData: FormData) => {
  const id_data_berkas_siswa = newData.get("id_data_berkas_siswa");
  const id_sys_periode = newData.get("id_sys_periode");
  const response = await api({
    url: "/siswa/penerimaan/smbjm/data-berkas/delete",
    method: "DELETE",
    data: { id_data_berkas_siswa, id_sys_periode },
  });
  return response.data;
};

export default function Form({
  method,
  closeModal,
  selectedRow,
  selectedBerkas,
  idDataPendaftaran,
}: {
  method: "POST" | "PATCH" | "DELETE";
  closeModal: () => void;
  selectedRow?: SysBerkas | null;
  selectedBerkas: DataBerkas | null;
  idDataPendaftaran?: string;
}) {
  const [sessionPeriode] = useSessionStorage("sessionPeriode", {
    id_sys_periode: "",
  });

  const [form, setForm] = useState<DataBerkas>({
    id_data_berkas_siswa: "",
    id_data_pendaftaran: idDataPendaftaran || "",
    id_sys_berkas: String(selectedRow?.id_sys_berkas) || "",
    nomor: selectedBerkas?.nomor || "",
  });
  const [file, setFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const isPost = method === "POST";
  const isDelete = method === "DELETE";

  const queryClient = useQueryClient();
  const { mutate, isPending, error } = useMutation({
    mutationFn: isPost ? postData : isDelete ? deleteData : undefined,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["listDataBerkasPenerimaan"] });
      queryClient.invalidateQueries({ queryKey: ["dataStep"] });
      closeModal();
      toast.success(data?.message || "Berhasil disimpan.");
    },
    onError: (
      error: AxiosError<{
        message?: string;
        errors?: Record<string, string[]>;
      }>,
    ) => {
      const err = error as AxiosError<{
        message?: string;
        errors?: Record<string, string[]>;
      }>;
      toast.error(
        err.response?.data?.message || "Terjadi kesalahan saat memproses data.",
      );
    },
  });

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files && e.target.files[0];
    if (selectedFile) setFile(selectedFile);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) setFile(droppedFile);
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    event.stopPropagation();
    const formData = new FormData();
    if (isDelete) {
      formData.append(
        "id_data_berkas_siswa",
        selectedBerkas?.id_data_berkas_siswa || "",
      );
    }
    formData.append("id_data_pendaftaran", form.id_data_pendaftaran);
    formData.append("id_sys_berkas", form.id_sys_berkas);
    formData.append("nomor", form.nomor || "");
    if (file) formData.append("file_berkas", file);
    if (sessionPeriode?.id_sys_periode)
      formData.append("id_sys_periode", sessionPeriode?.id_sys_periode);
    mutate(formData);
  };

  // --- MODE HAPUS ---
  if (isDelete) {
    return (
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="flex flex-col items-center text-center space-y-3 pt-4">
          <div className="p-3 bg-red-100 rounded-full text-red-600 dark:bg-red-900/30 dark:text-red-400">
            <Trash2 className="w-8 h-8" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-slate-900 dark:text-white">
              Hapus Berkas?
            </h2>
            <p className="text-sm text-slate-500 dark:text-slate-400 max-w-xs mx-auto">
              Apakah Anda yakin ingin menghapus berkas{" "}
              <span className="font-semibold text-slate-800 dark:text-slate-200">
                "{selectedBerkas?.sys_berkas?.jenis_berkas?.nama_jenis_berkas}"
              </span>
              ? Tindakan ini tidak dapat dibatalkan.
            </p>
          </div>
        </div>

        <div className="flex gap-3 justify-center">
          <Button
            type="button"
            variant="outline"
            disabled={isPending}
            onClick={() => closeModal()}
            className="w-full"
          >
            Batal
          </Button>
          <Button
            variant="destructive"
            disabled={isPending}
            className="w-full bg-red-600 hover:bg-red-700"
          >
            {isPending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              "Ya, Hapus"
            )}
          </Button>
        </div>
      </form>
    );
  }

  // --- MODE UPLOAD ---
  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <FormHeader
        icon={<UploadCloud />}
        title={`Upload ${selectedRow?.jenis_berkas?.nama_jenis_berkas}`}
        description={
          selectedRow?.keterangan_berkas ||
          "Unggah dokumen pendukung Anda di sini."
        }
      />

      <div className="space-y-4">
        {/* Dropzone Area */}
        <div
          className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors cursor-pointer ${
            file
              ? "border-blue-500 bg-blue-50 dark:bg-blue-900/10"
              : "border-slate-300 hover:border-blue-400 hover:bg-slate-50 dark:border-slate-700 dark:hover:bg-slate-800/50"
          }`}
          onDragOver={(e) => e.preventDefault()}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf" // Bisa disesuaikan dynamic dari selectedRow
            className="hidden"
            onChange={handleFileChange}
          />

          {file ? (
            <div className="flex flex-col items-center gap-2">
              <div className="p-3 bg-white rounded-full shadow-sm text-blue-600">
                <FileText className="w-8 h-8" />
              </div>
              <div>
                <p className="font-medium text-slate-900 dark:text-white truncate max-w-[250px]">
                  {file.name}
                </p>
                <p className="text-xs text-slate-500">
                  {(file.size / 1024).toFixed(0)} KB
                </p>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="mt-2 text-red-500 hover:text-red-600 hover:bg-red-50"
                onClick={(e) => {
                  e.stopPropagation();
                  setFile(null);
                  if (fileInputRef.current) fileInputRef.current.value = "";
                }}
              >
                Ganti File
              </Button>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-2 text-slate-500">
              <UploadCloud className="w-10 h-10 text-slate-300 dark:text-slate-600" />
              <div>
                <p className="font-medium text-slate-700 dark:text-slate-300">
                  Klik untuk upload
                </p>
                <p className="text-xs">atau drag and drop file di sini</p>
              </div>
            </div>
          )}
        </div>

        {/* File Info & Requirements */}
        <div className="bg-slate-50 dark:bg-slate-900 rounded-lg p-3 text-xs text-slate-600 dark:text-slate-400 space-y-2 border border-slate-100 dark:border-slate-800">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-3.5 h-3.5" />
            <span className="font-semibold">Ketentuan File:</span>
          </div>
          <div className="grid grid-cols-2 gap-2 pl-5">
            <div>
              <span className="block text-slate-400">Ekstensi:</span>
              <div className="flex gap-1 mt-0.5">
                {selectedRow?.extension_allowed &&
                selectedRow.extension_allowed.length > 0 ? (
                  selectedRow.extension_allowed.map((ext) => (
                    <Badge
                      key={ext}
                      variant="secondary"
                      className="px-1.5 py-0 text-[10px] bg-white border uppercase"
                    >
                      {ext}
                    </Badge>
                  ))
                ) : (
                  <Badge
                    variant="secondary"
                    className="px-1.5 py-0 text-[10px] bg-white border"
                  >
                    PDF
                  </Badge>
                )}
              </div>
            </div>
            <div>
              <span className="block text-slate-400">Maksimal Ukuran:</span>
              <span className="font-medium">
                {selectedRow?.max_upload ? `${selectedRow.max_upload} KB` : "-"}
              </span>
            </div>
          </div>
        </div>

        {/* Optional: Nomor Dokumen Input */}
        {selectedRow?.jenis_berkas?.is_add_filed_tambahan ? (
          <div className="space-y-1.5 pt-2">
            <Label className="flex justify-between">
              <span>Nomor Dokumen</span>
              {selectedRow?.jenis_berkas?.slug_jenis_berkas === "DTKS" && (
                <span className="text-[10px] text-blue-600 font-normal italic">
                  *NIK / Nama Penerima
                </span>
              )}
            </Label>
            <Input
              required
              placeholder={`Masukkan nomor ${selectedRow.jenis_berkas.nama_jenis_berkas}...`}
              value={form.nomor}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, nomor: e.target.value }))
              }
            />
            <InputError
              text={error?.response?.data?.errors?.nomor?.join(",")}
            />
          </div>
        ) : (
          ""
        )}
      </div>

      <div className="flex gap-3 pt-2">
        <Button
          variant="outline"
          className="flex-1"
          onClick={closeModal}
          disabled={isPending}
        >
          Batal
        </Button>
        <Button type="submit" className="flex-1" disabled={isPending || !file}>
          {isPending ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Mengupload...
            </>
          ) : (
            "Simpan Berkas"
          )}
        </Button>
      </div>
    </form>
  );
}
